import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RecipeService from '../../services/recipe.js';
import { Player } from '@lottiefiles/react-lottie-player';
import loader from '../../loader/main_loading_animation.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDumbbell,
  faClockRotateLeft,
  faBoltLightning,
  faClock,
  faFire,
  faChevronRight,
  faLeaf
} from '@fortawesome/free-solid-svg-icons';

const RefreshAndRefuel = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchRecipes();
  }, [page, activeFilter]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      // Using the updated no-auth service method
      const options = {
        page,
        limit: 9,
        filter: activeFilter !== 'all' ? activeFilter : undefined
      };

      const data = await RecipeService.getRecipesByCollection('refresh-and-refuel', options);

      if (data && data.length > 0) {
        setRecipes(prev => page === 1 ? data : [...prev, ...data]);
        setHasMore(data.length === 9);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setPage(1); // Reset to first page
  };

  const getProteinContent = (recipe) => {
    if (recipe.nutrition_table) {
      const match = recipe.nutrition_table.match(/Protein: (\d+)g/);
      return match ? match[1] : '20';
    }
    return '20';
  };

  return (
    // Add z-index-0 class to ensure this content stays beneath the mobile menu
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 z-0 relative">
      {/* Hero Section - Same image but different gradient overlay */}
      <div className="relative rounded-xl overflow-hidden mb-12">
        {/* Changed gradient overlay while keeping the same image */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#232526] to-[#991515]/80 z-[1]"></div>
        <img
          src="/workout-meal-hero.jpg"
          alt="Post-workout meals"
          className="w-full h-64 md:h-80 object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1490645935967-10de6ba17061';
          }}
        />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12 z-[2]">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Refresh <span className="text-[#FFD700]">&</span> Refuel
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Optimize muscle recovery and replenish energy after your workout with these specially
            crafted protein-rich recipes.
          </p>
        </div>
      </div>

      {/* Nutrition Guide Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-[#0e4621] mb-6 flex items-center">
          <span className="w-8 h-1 bg-[#DAA520] block mr-3"></span>
          Post-Workout Nutrition Guide
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#0e4621]">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0e4621]/10 text-[#0e4621]">
                <FontAwesomeIcon icon={faClockRotateLeft} className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold ml-4">Timing Matters</h3>
            </div>
            <p className="text-gray-700">
              Consume a combination of carbohydrates and protein within 30-60 minutes after your workout
              for optimal recovery and muscle protein synthesis.
            </p>
          </div>

          {/* Changed Carbohydrates from gold to green */}
        

          {/* Changed Protein from green to gold */}
          <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#DAA520]">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#DAA520]/10 text-[#DAA520]">
                <FontAwesomeIcon icon={faDumbbell} className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold ml-4">Protein</h3>
            </div>
            <p className="text-gray-700">
              Aid muscle repair with quality protein sources like chicken, fish, eggs, Greek yogurt,
              or plant-based options such as tofu and legumes.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#0e4621]">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0e4621]/10 text-[#0e4621]">
                <FontAwesomeIcon icon={faBoltLightning} className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold ml-4">Carbohydrates</h3>
            </div>
            <p className="text-gray-700">
              Replenish glycogen stores with simple carbs like fruits and complex carbs like whole grains,
              sweet potatoes, and brown rice.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-[#0e4621] mb-6 flex items-center">
          <span className="w-8 h-1 bg-[#DAA520] block mr-3"></span>
          Recovery Recipes
        </h2>


      </div>

      {/* Recipe Grid */}
      {loading && page === 1 ? (
        <div className="flex justify-center py-20">
          <Player src={loader} className="player w-32" loop autoplay />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recipes.map((recipe) => (
              <Link
                to={`/recipes/${recipe.slug}`}
                key={recipe._id}
                className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={recipe.media?.heroImageUrl || '/empty-image.png'}
                    alt={recipe.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  {recipe.times?.totalTime && (
                    <div className="absolute top-4 left-4 bg-white/90 rounded-full px-3 py-1 text-sm font-medium text-[#0e4621] flex items-center">
                      <FontAwesomeIcon icon={faClock} className="mr-1.5 h-3.5 w-3.5" />
                      {recipe.times.totalTime} min
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-[#DAA520]/90 rounded-full px-3 py-1 text-sm font-medium text-white flex items-center">
                    <FontAwesomeIcon icon={faDumbbell} className="mr-1.5 h-3.5 w-3.5" />
                    {getProteinContent(recipe)}g protein
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-[#0e4621] transition-colors duration-200 mb-2">
                    {recipe.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {recipe.description?.substring(0, 120) || "A perfect post-workout meal to help you recover and rebuild muscle."}
                    {recipe.description?.length > 120 ? "..." : ""}
                  </p>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faFire} className="text-[#DAA520] mr-2" />
                      <span className="text-sm text-gray-700">
                        {recipe.calories?.low || "350"} cal
                      </span>
                    </div>

                    <span className="text-[#0e4621] font-medium text-sm flex items-center">
                      View Recipe
                      <FontAwesomeIcon icon={faChevronRight} className="ml-1 h-3 w-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Empty State */}
          {recipes.length === 0 && !loading && (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-200">
                <FontAwesomeIcon icon={faDumbbell} className="text-gray-400 h-10 w-10" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No recipes found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your filters or check back later</p>
              <button
                onClick={() => handleFilterChange('all')}
                className="px-6 py-2 bg-[#0e4621] text-white rounded-full hover:bg-[#0a3419] transition-colors"
              >
                View All Recipes
              </button>
            </div>
          )}

          {/* Loading More & Load More Button */}
          {loading && page > 1 && (
            <div className="flex justify-center mt-8">
              <Player src={loader} className="player w-16" loop autoplay />
            </div>
          )}

          {!loading && hasMore && recipes.length > 0 && (
            <div className="flex justify-center mt-10">
              <button
                onClick={loadMore}
                className="px-8 py-3 bg-white border-2 border-[#0e4621] text-[#0e4621] font-medium rounded-full hover:bg-[#0e4621] hover:text-white transition-all duration-300 shadow-sm"
              >
                Load More Recipes
              </button>
            </div>
          )}
        </>
      )}

      {/* Bottom CTA - only show if user is not logged in */}
      {!localStorage.getItem("userToken") &&
        <div className="mt-16 bg-gradient-to-r from-[#0e4621] to-[#0a3419] rounded-xl p-8 text-white shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h2 className="text-2xl font-bold mb-2">Want personalized post-workout recipes?</h2>
              <p className="text-white/80">Get recipes tailored to your fitness goals and dietary preferences.</p>
            </div>
            <Link to="/register" className="px-6 py-3 bg-[#DAA520] text-white rounded-full hover:bg-[#c99515] transition-colors shadow-md font-medium">
              Create Account
            </Link>
          </div>
        </div>
      }
    </div>
  );
};

export default RefreshAndRefuel;
